import React from 'react';
import { ValueType } from 'react-select/lib/types';

import { IFilter, IProduct, KeyValuePair, OptionType } from '../models';
import CardRadioButton from './ui-components/CardRadioButton';
import ListSelector from './ui-components/ListSelector';
import NumberInput from './ui-components/NumberInput';
import SliderComponent from './ui-components/SliderComponent';
import TextInput from './ui-components/TextInput';

export default class ComponentFactory {

  static getUniqueValuesForKey = (key: string, products: IProduct[]): KeyValuePair[] => {
    let uniqueValues: KeyValuePair[] = [];
    products.forEach(
      (p) => uniqueValues = [...uniqueValues, ...p.metadata.filter(
        (data) => data.key === key
          && !uniqueValues.some((item) => item.value === data.value))]);

    return uniqueValues;
  }

  static renderRadio = (
    filter: IFilter,
    products: IProduct[],
    uiState: Map<string, string>,
    onClick: (key: string, value: string) => void) => {

    return ComponentFactory.getUniqueValuesForKey(filter.key, products).map((obj) => {
      const selected = uiState.get(filter.key) === obj.value;

      return (
        <CardRadioButton
          selected={selected}
          key={obj.value}
          obj={obj}
          onClick={(key: string, value: string) => { onClick(key, value); }}
        />
      );
    });
  }

  static renderSelect = (
    filter: IFilter,
    products: IProduct[],
    uiState: Map<string, string>,
    addUiStateItem: (key: string, value: string) => void) => {

    const options: Array<{ value: string, label: string }> = [];
    let unit = '';

    ComponentFactory.getUniqueValuesForKey(filter.key, products).forEach((obj) => {
      if (obj.unit) {
        unit = obj.unit;
      }

      options.push({ value: obj.value, label: obj.value + (obj.unit != null ? ' ' + obj.unit : '') });
      return options;
    });

    if (!uiState.has(filter.key)) {
      addUiStateItem(filter.key, '');
    }

    const onChange = (selectedOption: ValueType<OptionType>) => {
      if (!selectedOption) return;
      addUiStateItem(filter.key, (selectedOption as OptionType).value);
    };
    return (
      <ListSelector
        title={filter.key}
        key={filter.key}
        items={options}
        unit={unit}
        onChange={onChange}
        selectedValue={uiState.get(filter.key)}
      />
    );
  }

  static renderSlider = (
    filter: IFilter,
    products: IProduct[],
    uiState: Map<string, string>,
    onChange: (lower: number, upper: number) => void) => {
    const values: number[] = [];
    const defaultMin = 500;
    const defaultMax = 1500;
    const selectedValues = uiState.get(filter.key);

    let unit = '';

    ComponentFactory.getUniqueValuesForKey(filter.key, products).forEach((obj) => {
      values.push(parseInt(obj.value));
      if (obj.unit) unit = obj.unit;
    });

    // If no values or just 1 value, set default range
    if (values.length <= 1) {
      values.push(defaultMin);
      values.push(defaultMax);
    }

    let min = Math.min(...values);
    let max = Math.max(...values);

    if (selectedValues) {
      min = parseInt(selectedValues.substring(0, selectedValues.indexOf(',')));
      max = parseInt(selectedValues.substring(selectedValues.indexOf(',') + 1));
    }

    return (
      <SliderComponent
        max={Math.max(...values)}
        min={Math.min(...values)}
        key={filter.key}
        unit={unit}
        values={[min, max]}
        onChange={onChange}
      />
    );
  }

  static renderTextInput = (
    filter: IFilter,
    onChange: (label: string, value: string) => void) => {
    return (
      <TextInput
        key={filter.key}
        label={filter.key}
        onChange={onChange}
        mandatory={false}
      />
    );
  }

  static renderNumberInput = (
    filter: IFilter,
    onChange: (label: string, value: string) => void) => {
    return (
      <NumberInput
        key={filter.key}
        label={filter.key}
        onChange={onChange}
      />
    );
  }

  static build(
    filter: IFilter,
    products: IProduct[],
    uiState: Map<string, string>,
    addUiStateItem: (key: string, value: string) => void) {

    switch (filter.component) {
      case 'radio':
        return this.renderRadio(filter, products, uiState, addUiStateItem);
      case 'select':
        return this.renderSelect(filter, products, uiState, addUiStateItem);
      case 'slider':
        return this.renderSlider(
          filter,
          products,
          uiState,
          (lower, upper) => addUiStateItem(filter.key, `${lower},${upper}`));
      case 'textinput':
        return this.renderTextInput(filter, addUiStateItem);
      case 'numberinput':
        return this.renderNumberInput(filter, addUiStateItem);
      default:
        return <div />;
    }
  }
}
